"use client";

import { useMemo } from "react";
import type { Option } from "@/components/tool-kit/primitives";
import {
  SourceCitationBuilder,
  type SourceField,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  type BoardGameLabels,
  boardGameIsReady,
  BOARD_GAME_FIELDS as F,
  formatBoardGame,
  type GameKind,
} from "@/lib/tool-kit/citation-sources-books/board-game-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const BOARD_GAME_CITATION_GENERATOR_STRINGS = {
  formHeading: "Game details",
  resultsHeading: "Your citations",
  emptyPrompt: "Enter the game's title to see your citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  kindLabel: "What kind of game is it?",
  kindBoard: "Board game",
  kindCard: "Card game",
  kindDice: "Dice game",
  kindTabletop: "Tabletop game",
  kindRoleplaying: "Role-playing game",
  designersLabel: "Designer(s)",
  designersPlaceholder: "Matt Leacock",
  designersHint:
    "One per line, as printed on the box or rulebook, as First Last or Last, First. Leave blank for a game credited only to a company.",
  titleLabel: "Title of the game",
  titlePlaceholder: "Pandemic",
  titleHint:
    "Sentence case for APA: capitalize only the first word, the first word after a colon and proper nouns. MLA and Chicago get title case automatically.",
  editionLabel: "Edition (optional)",
  editionPlaceholder: "2nd",
  publisherLabel: "Publisher",
  publisherPlaceholder: "Z-Man Games",
  yearLabel: "Year of the edition you played",
  yearPlaceholder: "2008",
  publisherAuthorLabel:
    "Use the publisher as the APA author (for a game credited to a company)",
  urlLabel: "URL of the rules or the publisher's page (optional)",
  urlPlaceholder: "https://www.zmangames.com/",
  shortTitleLabel: "Short title for notes (Chicago, optional)",
  shortTitlePlaceholder: "Pandemic",
  shortTitleHint: "Used in the shortened note. Leave blank for the main title.",
  apaLabel: "APA 7th edition",
  mlaLabel: "MLA 9th edition",
  chicagoLabel: "Chicago 18th edition (notes and bibliography)",
  referenceList: "Reference list",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  worksCited: "Works Cited",
  inText: "In-text citation",
  footnote: "Footnote",
  shortNote: "Shortened note",
  bibliography: "Bibliography",
  apaNote:
    "We found no APA 7 board-game example. This follows its four-element rule: the designer as author with a (Designer) label, the year, the italic title with [Board game] after it, and the publisher. A game credited to a company takes the company as author.",
  mlaNote:
    "We found no MLA 9 board-game example. This adapts the MLA Style Center's video-game model: the title first, Designed by in the contributor slot, the publisher and year, and the kind of game at the end. In text, the title stands in for an author.",
  chicagoNote:
    "We found no Chicago 18 board-game example. This puts the designer first like an author, names the kind of game after the title the way Chicago names a medium, and gives the publisher and year in the usual form. A convention, not a published example.",
  example: {
    kind: "board",
    designers: "Matt Leacock",
    title: "Pandemic",
    publisher: "Z-Man Games",
    year: "2008",
  },
} as const;

type Strings = typeof BOARD_GAME_CITATION_GENERATOR_STRINGS;

function kindOptions(strings: Strings): readonly Option<GameKind>[] {
  return [
    { value: "board", label: strings.kindBoard },
    { value: "card", label: strings.kindCard },
    { value: "dice", label: strings.kindDice },
    { value: "tabletop", label: strings.kindTabletop },
    { value: "roleplaying", label: strings.kindRoleplaying },
  ];
}

function buildFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.kind,
      kind: "select",
      label: strings.kindLabel,
      options: kindOptions(strings),
    },
    {
      id: F.designers,
      kind: "people",
      label: strings.designersLabel,
      placeholder: strings.designersPlaceholder,
      hint: strings.designersHint,
    },
    {
      id: F.title,
      kind: "text",
      label: strings.titleLabel,
      placeholder: strings.titlePlaceholder,
      hint: strings.titleHint,
    },
    {
      id: F.edition,
      kind: "text",
      label: strings.editionLabel,
      placeholder: strings.editionPlaceholder,
    },
    {
      id: F.publisher,
      kind: "text",
      label: strings.publisherLabel,
      placeholder: strings.publisherPlaceholder,
    },
    {
      id: F.year,
      kind: "date",
      label: strings.yearLabel,
      placeholder: strings.yearPlaceholder,
    },
    {
      id: F.publisherAuthor,
      kind: "checkbox",
      label: strings.publisherAuthorLabel,
    },
    {
      id: F.url,
      kind: "url",
      label: strings.urlLabel,
      placeholder: strings.urlPlaceholder,
      wide: true,
    },
    {
      id: F.shortTitle,
      kind: "text",
      label: strings.shortTitleLabel,
      placeholder: strings.shortTitlePlaceholder,
      hint: strings.shortTitleHint,
    },
  ];
}

/** The widget's strings, as the formatter's labels. */
export function boardGameLabels(strings: Strings): BoardGameLabels {
  return {
    apa: strings.apaLabel,
    mla: strings.mlaLabel,
    chicago: strings.chicagoLabel,
    referenceList: strings.referenceList,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    worksCited: strings.worksCited,
    inText: strings.inText,
    footnote: strings.footnote,
    shortNote: strings.shortNote,
    bibliography: strings.bibliography,
    apaNote: strings.apaNote,
    mlaNote: strings.mlaNote,
    chicagoNote: strings.chicagoNote,
  };
}

export default function BoardGameCitationGeneratorWidget() {
  const strings = useToolUi(BOARD_GAME_CITATION_GENERATOR_STRINGS);
  const fields = useMemo(() => buildFields(strings), [strings]);
  const labels = useMemo(() => boardGameLabels(strings), [strings]);
  const format = useMemo(
    () => (values: Record<string, string>) => formatBoardGame(values, labels),
    [labels]
  );

  return (
    <SourceCitationBuilder
      example={strings.example}
      fields={fields}
      format={format}
      isReady={boardGameIsReady}
      strings={strings}
    />
  );
}
